import React from 'react';

const AISettings = ({
  isAiTrainingExpanded,
  setAiTrainingExpanded,
  aiTonePreference,
  setAiTonePreference,
  aiCustomPrompt,
  setAiCustomPrompt,
  writingSamples,
  setWritingSamples,
  handleSaveAIPreferences,
  isCustomAnalysisExpanded,
  setCustomAnalysisExpanded,
  customCriteria,
  setCustomCriteria,
  handleSaveCustomCriteria,
  isSurveyExpanded,
  setSurveyExpanded,
  surveyTitle,
  setSurveyTitle,
  handleSaveSurveyTitle,
  surveyQuestions,
  handleDeleteQuestion,
  newQuestion,
  setNewQuestion,
  isMultipleChoice,
  setIsMultipleChoice,
  options,
  setOptions,
  newOption,
  setNewOption,
  handleAddQuestion,
  customSurveyCriteria,
  setCustomSurveyCriteria,
  handleSaveCustomSurveyCriteria,
  publicSurveyLink,
  copyPublicSurveyLink,
  message,
}) => {
  return (
    <div className="ai-settings-container">

      {/* AI Training */}
      <div className="ai-training-section">
        <h3
          className="collapsible-title"
          onClick={() => setAiTrainingExpanded(!isAiTrainingExpanded)}
        >
          <span>AI personalization</span>
          <span>{isAiTrainingExpanded ? '▴' : '▾'}</span>
        </h3>
        {isAiTrainingExpanded && (
          <div className="survey-content">
            <div className="form-group">
              <label htmlFor="aiTonePreference">Tone preference</label>
              <select
                id="aiTonePreference"
                value={aiTonePreference}
                onChange={(e) => setAiTonePreference(e.target.value)}
                className="settings-input"
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="concise">Concise</option>
                <option value="humorous">Humorous</option>
                <option value="formal">Formal</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="aiCustomPrompt">Custom style prompt</label>
              <textarea
                id="aiCustomPrompt"
                rows="4"
                value={aiCustomPrompt}
                onChange={(e) => setAiCustomPrompt(e.target.value)}
                className="custom-criteria-textarea"
                placeholder="e.g. Use short sentences. I like bullet points and a confident tone."
              />
            </div>
            <div className="form-group">
              <label htmlFor="writingSamples">Writing samples</label>
              <textarea
                id="writingSamples"
                rows="5"
                value={writingSamples.join('\n\n')}
                onChange={(e) => setWritingSamples(e.target.value.split(/\n\s*\n/))}
                className="custom-criteria-textarea"
                placeholder="Paste 1 or more writing samples, separated by a blank line"
              />
              <small style={{ color: 'var(--text-light)', fontSize: '0.82rem' }}>
                Separate multiple samples with a blank line.
              </small>
            </div>
            <button className="save-button" onClick={handleSaveAIPreferences}>
              Save AI preferences
            </button>
          </div>
        )}
      </div>

      {/* Custom Analysis Criteria */}
      <div className="custom-criteria-section">
        <h3
          className="collapsible-title"
          onClick={() => setCustomAnalysisExpanded(!isCustomAnalysisExpanded)}
        >
          <span>Custom analysis criteria</span>
          <span>{isCustomAnalysisExpanded ? '▴' : '▾'}</span>
        </h3>
        {isCustomAnalysisExpanded && (
          <div className="survey-content">
            <textarea
              value={customCriteria}
              onChange={(e) => setCustomCriteria(e.target.value)}
              rows="6"
              className="custom-criteria-textarea"
              placeholder="Enter your custom analysis criteria..."
            />
            <button className="save-button" onClick={handleSaveCustomCriteria}>
              Save criteria
            </button>
          </div>
        )}
      </div>

      {/* Survey Settings */}
      <h3
        className="collapsible-title"
        onClick={() => setSurveyExpanded(!isSurveyExpanded)}
      >
        <span>Survey settings</span>
        <span>{isSurveyExpanded ? '▴' : '▾'}</span>
      </h3>
      {isSurveyExpanded && (
        <div className="survey-section">
          <div className="survey-content">

            {/* Survey Title */}
            <div className="form-group">
              <label>Survey title</label>
              <input
                type="text"
                value={surveyTitle}
                onChange={(e) => setSurveyTitle(e.target.value)}
                className="settings-input"
                placeholder="Enter survey title..."
              />
              <button className="save-button" onClick={handleSaveSurveyTitle}>
                Save title
              </button>
            </div>

            {/* Survey Questions */}
            <div className="survey-questions-section">
              <h4>Survey questions</h4>
              {surveyQuestions.length > 0 ? (
                surveyQuestions.map((q, index) => (
                  <div key={q._id || index} className="survey-question">
                    <p>{q.questionText || 'No question text available'}</p>
                    {q.isMultipleChoice && q.options && (
                      <ul>
                        {q.options.map((option, idx) => (
                          <li key={idx}>{option}</li>
                        ))}
                      </ul>
                    )}
                    <button className="remove-sync-btn" onClick={() => handleDeleteQuestion(q._id)}>
                      Delete
                    </button>
                  </div>
                ))
              ) : (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>No survey questions yet</p>
              )}

              <div className="form-group" style={{ marginTop: '1rem' }}>
                <input
                  type="text"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  placeholder="Enter a new question"
                  className="settings-input"
                />
                <div className="form-group" style={{ marginTop: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={isMultipleChoice}
                      onChange={(e) => setIsMultipleChoice(e.target.checked)}
                    />
                    Multiple choice question?
                  </label>
                </div>
                {isMultipleChoice && (
                  <div className="multiple-choice-options">
                    <div className="options-container">
                      <input
                        type="text"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        placeholder="Enter an option"
                        className="settings-input"
                      />
                      <button
                        className="settings-btn"
                        onClick={() => {
                          if (newOption.trim()) {
                            setOptions((prev) => [...prev, newOption]);
                            setNewOption('');
                          }
                        }}
                      >
                        Add option
                      </button>
                    </div>
                    <ul>
                      {options.map((option, index) => (
                        <li key={index}>
                          {option}
                          <button onClick={() => setOptions(options.filter((o, i) => i !== index))}>
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button className="settings-btn" onClick={handleAddQuestion} style={{ marginTop: '0.75rem' }}>
                  Add question
                </button>
              </div>
            </div>

            {/* Custom Survey Criteria */}
            <div className="custom-criteria-section" style={{ marginTop: '1.5rem' }}>
              <h4>Custom survey criteria</h4>
              <textarea
                value={customSurveyCriteria}
                onChange={(e) => setCustomSurveyCriteria(e.target.value)}
                rows="4"
                className="custom-criteria-textarea"
                placeholder="Enter custom survey criteria..."
              />
              {message && <p className="error-message">{message}</p>}
              <button className="save-button" onClick={handleSaveCustomSurveyCriteria}>
                Save criteria
              </button>
            </div>

            {/* Public Survey Link */}
            <div className="public-survey-section" style={{ marginTop: '1.5rem' }}>
              <h4>Public survey link</h4>
              <div className="input-group">
                <input
                  type="text"
                  readOnly
                  value={publicSurveyLink}
                  className="public-survey-input"
                  onClick={(e) => e.target.select()}
                />
                <button onClick={copyPublicSurveyLink} className="copy-link-btn">
                  Copy link
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default AISettings;
